<template>
    <div
        class="contact-list-item"
        :class="{
            active: isActive,
            unreaded: contact.unread,
        }"
        @click="clickConversation"
        @contextmenu.prevent="onContextMenu"
    >
            <div class="avatar-section">
                <div class="contact-avatar-wrapper">
                    <ContactAvatar :contact="contact" :badge="false" :size="'small'" />
                    <div
                        class="platform-badge"
                        v-tippy="{ followCursor: true, placement: 'bottom-end', delay: [300, 0] }"
                        :content="platformTooltip"
                    >
                        <PlatformIcon :platform="channelType" :size="10" :tippy-disabled="true" />
                    </div>
                </div>
            </div>

            <div class="content-section">
                <div class="contact-name">
                    {{ fullname }}
                </div>
                <ContactLastMessage :lastMessage="contact.lastMessage" />
            </div>

            <div class="status-section">
                <div class="vip-indicator" v-if="contact.vipCustomer">
                    <i
                        class="material-icons vip-star"
                        v-tippy="{ followCursor: true, placement: 'bottom-end', delay: [300, 0] }"
                        :content="'★ VIP Customer'"
                    >
                        star
                    </i>
                </div>
                <div class="timestamp-wrapper">
                    <div class="chat-timestamp" v-tippy="{ followCursor: true, placement: 'bottom-end' }" :content="fullTime">
                        {{ displayTime }}
                    </div>
                    <div class="unread-bagde-container" v-if="contact.unread">
                        <MessagesUnreadBagde />
                    </div>
                    <div
                        class="chat-owner-text"
                        :class="{
                            [getChatOwnerStatus()]: true,
                        }"
                    >
                        {{ getChatOwnerText() }}
                    </div>
                </div>
            </div>
        </div>
</template>
<script>
import { maskContactInfo, SanitizeHtml } from "../../../utils/utils";
import ContactAvatar from "./ContactAvatar.vue";
import { mapState, mapActions } from "vuex";
import moment from "moment";
import PlatformIcon from "./ChatMessageArea/components/PlatformIcon.vue";
import ContactLastMessage from "./ContactLastMessage.vue";
import _ from "lodash";
import MessagesUnreadBagde from "../../svg/icons/MessagesUnreadBagde.vue";
import { log2 } from "../../../utils/log2";

const log = log2("GridContactListItem");

export default {
    name: "GridContactListItem",
    components: {
        PlatformIcon,
        ContactAvatar,
        ContactLastMessage,
        MessagesUnreadBagde,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        activeContact: {
            type: Object,
            default: () => ({}),
        },
        currentTime: {
            type: Number,
            default: 0,
        },
    },
    data: () => ({
        cachedDisplayTime: null,
    }),
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
            users: (state) => state.user.users.data,
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            selectedSortField: (state) => state.contact.selectedSortField,
        }),
        channelType() {
            if (this.contact.platform === "whatsapp") {
                const channel = this.channels.find((channel) => channel._id === this.contact.channel);
                return channel ? channel.type : "notWp";
            }
            return this.contact.platform;
        },
        channelName() {
            if (this.contact.platform === "whatsapp") {
                const channel = this.channels.find((channel) => channel._id === this.contact.channel);
                return channel ? channel.name : "";
            }
            return "";
        },
        displayTime() {
            const lastChatStatus = _.get(this.contact, "lastConversationStatus", "destroyed");
            const isActiveChat = ["new", "active", "chatbot"].includes(lastChatStatus);
            if (isActiveChat) {
                return this.fromNow();
            }
            if (!this.cachedDisplayTime) {
                this.cachedDisplayTime = this.fromNow();
            }
            return this.cachedDisplayTime;
        },
        fullTime() {
            return moment(this.contact?.lastConversation?.createdDate).format("LLLL");
        },
        isActive() {
            return this.contact && this.activeContact?._id === this.contact?._id;
        },
        platformTooltip() {
            switch (this.channelType) {
                case "web":
                    return "Web";
                case "whatsapp":
                case "whatsapp-v2":
                    return `WhatsApp${this.channelName ? ` (${this.channelName})` : ""}`;
                case "whatsapp-business-api":
                case "whatsapp-business-api-supsis":
                    return `WhatsApp Business${this.channelName ? ` (${this.channelName})` : ""}`;
                case "instagram":
                    return `Instagram${this.channelName ? ` (${this.channelName})` : ""}`;
                case "messenger":
                    return `Messenger${this.channelName ? ` (${this.channelName})` : ""}`;
                case "telegram":
                    return "Telegram";
                case "email":
                    return "E-posta";
                case "sms":
                    return "SMS";
                default:
                    return this.channelType || "Bilinmeyen Platform";
            }
        },
        isPhoneNumber() {
            return this.contact.fullname === this.contact.whatsappNumber;
        },
        fullname() {
            const fullname = SanitizeHtml(this.contact.fullname) || "N/A";
            return this.isPhoneNumber ? maskContactInfo(fullname, 5) : fullname;
        },
    },
    methods: {
        onContextMenu(event) {
            const rect = event.currentTarget.getBoundingClientRect();
            const x = event.clientX;
            const y = event.clientY;
            
            this.$emit('show-context-menu', {
                event,
                contact: this.contact,
                x: x,
                y: y,
                elementRect: rect,
            });
        },
        fromNow() {
            const lastChatStatus = _.get(this.contact, "lastConversationStatus", "destroyed");
            const isActive = ["new", "active", "chatbot"].includes(lastChatStatus);
            const createdDate = this.selectedSortField === "lastActionTime" ? moment(this.contact?.lastActionTime) : moment(this.contact?.lastConversation?.createdDate);
            const now = this.currentTime || Date.now();
            const duration = moment.duration(moment(now).diff(createdDate));
            if (isActive) {
                if (duration < 30000) return "Just now";
                else if (duration >= 1000 * 60 * 60) {
                    return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
                } else {
                    return moment.utc(duration.asMilliseconds()).format("mm:ss");
                }
            } else {
                const isToday = moment(now).isSame(createdDate, "day");
                const isYesterday = moment(now).subtract(1, "days").isSame(createdDate, "day");
                if (duration < 30000) return "Just now";
                else if (isToday) {
                    return moment(createdDate).format("LT");
                } else if (isYesterday) return "Yesterday";
                else {
                    return moment(createdDate).format("DD/MM/YYYY HH:mm");
                }
            }
        },
        clickConversation() {
            if (this.contact) this.$emit("joinConversation");
        },
        getChatOwnerStatus() {
            //return you,others,chatbot,queue,closed
            const isAssignedToMe = this.contact?.lastConversation?.users?.includes(this.user._id);
            const isAssignedToAnotherAgent = !isAssignedToMe && this.contact?.lastConversation?.users?.length > 0;
            const isClosed = this.contact?.lastConversation?.status === "destroyed";
            const isInQueue = this.contact?.lastConversation?.status === "new";
            const isInChatbot = this.contact?.lastConversation?.status === "chatbot";

            if (isClosed) {
                return "closed";
            } else if (isInQueue) {
                return "queue";
            } else if (isAssignedToMe) {
                return "you";
            } else if (isInChatbot) {
                return "chatbot";
            } else if (isAssignedToAnotherAgent) {
                return "others";
            } else {
                return "closed";
            }
        },
        getChatOwnerText() {
            if (this.contact?.lastConversation?.startedFrom === "api") {
                return "API ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "broadcast") {
                return "Toplu mesaj ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "instagram-comment") {
                return "Instagram yorumu ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "trendyol") {
                return "Trendyol ürün sorusu ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "instagram-mention") {
                return "Instagram mention ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "page-comment") {
                return "Facebook yorumu ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "automation") {
                return "Otomasyon ile başlatıldı";
            } else if (this.contact?.lastConversation?.startedFrom === "voice-agent-call") {
                return "Telefon Araması ile başlatıldı";
            }

            if (this.contact?.lastConversation?.status === "new") {
                return "Temsilci bekleniyor";
            } else if (this.contact?.lastConversation?.status === "chatbot") {
                return "Chatbot ile görüşmede";
            } else if (this.contact?.lastConversation?.status === "active") {
                const users = this.contact.lastConversation?.users || [];
                const currentUser = this.user;
                const connectedUser = this.contact.lastConversation?.connectedUser;
                const allUsers = this.users;

                if (users.length === 0 && !connectedUser) {
                    return `Temsilci bekleniyor`;
                } else if (users.length === 1 && users[0] === currentUser._id) {
                    return "Seninle görüşmede";
                } else if (users.length === 1) {
                    const firstUser = allUsers.find((user) => user._id === users[0]);
                    return `${firstUser?.name} ile görüşmede`;
                } else if (users.length === 2) {
                    const firstUser = allUsers.find((user) => user._id === users[0]);
                    const secondUser = allUsers.find((user) => user._id === users[1]);
                    const isAssignedToMe = users.includes(currentUser._id);

                    if (isAssignedToMe) {
                        const otherUser = [firstUser, secondUser].find((user) => user?._id !== currentUser?._id);
                        return `Sen ve ${otherUser?.name} ile görüşmede`;
                    } else {
                        return `${firstUser?.name} ve ${secondUser?.name} ile görüşmede`;
                    }
                } else if (users.length > 1) {
                    let firstUser = allUsers.find((user) => user._id === users[0]);
                    const isAssignedToMe = users.includes(currentUser._id);

                    if (isAssignedToMe) {
                        firstUser = allUsers.find((user) => user._id === currentUser._id);
                    }

                    let firstUserName = firstUser ? firstUser.name : "Bilinmeyen";
                    if (isAssignedToMe) {
                        firstUserName = "Sen";
                    }

                    return `${firstUserName} ve ${users.length - 1} kişi ile görüşmede`;
                }
            } else if (this.contact?.lastConversation?.status === "destroyed") {
                const users = this.contact.lastConversation?.users || [];
                const connectedUser = this.contact.lastConversation?.connectedUser;
                const currentUser = this.user;
                const allUsers = this.users;

                if (users.length === 0 && !connectedUser) {
                    return `Kimse ilgilenmedi`;
                } else if (users.length === 0 && !!connectedUser) {
                    const firstUser = allUsers.find((user) => user._id === connectedUser);
                    return `Temsilci ${firstUser?.name} hattan düştü`;
                } else if (users.length === 1 && users[0] === currentUser._id) {
                    return "Seninle görüştü";
                } else if (users.length === 1) {
                    const firstUser = allUsers.find((user) => user._id === users[0]);
                    return `${firstUser?.name} ile görüştü`;
                } else if (users.length > 1) {
                    const firstUser = allUsers.find((user) => user._id === users[0]);
                    const firstUserName = firstUser ? firstUser.name : "Bilinmeyen";
                    return `${firstUserName} ve ${users.length - 1} kişi ile görüştü`;
                }
            }
            
            // Hiçbir koşul sağlanmazsa veya undefined durumunda
            return "Temsilci bekleniyor";
        },
    },
};
</script>
<style lang="scss">
.contact-list-item {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-bottom: 2px solid #f2f4f7;
    gap: 12px;
    min-height: 64px;
    position: relative;

    &:hover {
        background: var(--color-bg-neutral-default);
    }

    &.active {
        background: var(--color-bg-neutral-default);
    }

    &.unreaded {
        .contact-name {
            font-weight: 600;
        }

        .message-text {
            font-weight: 500;
        }
    }

    // Sol Taraf: Avatar + Platform Badge
    .avatar-section {
        flex-shrink: 0;

        .contact-avatar-wrapper {
            position: relative;

            .platform-badge {
                position: absolute;
                bottom: -2px;
                right: -2px;
                background: white;
                border: 2px solid white;
                border-radius: 50%;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                    transform: scale(1.1);
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
                }
            }
        }
    }

    // Orta Taraf: İçerik
    .content-section {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0; // Flex overflow fix

        .contact-name {
            font-size: 14px;
            font-weight: 500;
            color: #1f2937;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }

    // Sağ Taraf: Status
    .status-section {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
        min-width: 60px;

        .vip-indicator {
            .vip-star {
                font-size: 16px;
                background: -webkit-linear-gradient(#d6ea2d, #f960cb, #9037d4);
                background: linear-gradient(#d6ea2d, #f960cb, #9037d4);
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
            }
        }

        .timestamp-wrapper {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;

            .chat-timestamp {
                font-size: 12px;
                color: #374151;
                font-weight: 600;
                white-space: nowrap;
                line-height: normal;
            }

            .unread-bagde-container {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .chat-owner-text {
                line-height: normal;
                font-size: 9px;
                font-weight: 600;
                text-align: right;
                color: #754cd5;
                width: 100%;
                line-clamp: 1;
                -webkit-line-clamp: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                font-family: Inter, sans-serif;

                &.you {
                    color: #32cd32;
                }

                &.others {
                    color: #ffa500;
                }

                &.chatbot {
                    color: #87ceeb;
                }

                &.queue {
                    color: #9370db;
                }

                &.closed {
                    color: #d3d3d3;
                }
            }
        }
    }
}
</style>
